/**
 * Weight units union type
 */
/**
 * Import Weight type and Unit enum from generated GraphQL types
 * Unit type now uses lowerCamelCase and is the single source of truth for weight units
 */
import type { Weight, Unit } from '@rematter/frontend-lib-api-client';

// Re-export Unit for convenience
export type { Unit };

/**
 * Conversion factors to/from pounds
 */
export const CONVERSION_FACTORS: Record<Unit, number> = {
  Pounds: 1, // 1 pound = 1 pound
  Ounces: 1 / 16, // 1 ounce = 1/16 pound
  Kilograms: 2.20462, // 1 kilogram = 2.20462 pounds
  Grams: 0.00220462, // 1 gram = 0.00220462 pounds
  NetTons: 2000, // 1 net ton = 2000 pounds
  GrossTons: 2240, // 1 gross ton = 2240 pounds
  MetricTons: 2204.62, // 1 metric ton = 2204.62 pounds
  ShortTons: 2000, // 1 short ton = 2000 pounds (same as net ton)
  LongTons: 2240, // 1 long ton = 2240 pounds (same as gross ton)
  Each: 1, // 1 each = treated as 1 pound for conversion purposes
  ShortHundredweight: 100, // 1 short hundredweight = 100 pounds
  LongHundredweight: 112, // 1 long hundredweight = 112 pounds
  Meter: 0,
  Yard: 0,
};

/**
 * Weight unit information interface
 */
export interface UnitInfo {
  unit: Unit;
  symbol: string;
  name: string;
}

/**
 * Default weight units
 */
export const DEFAULT_WEIGHT_UNITS: UnitInfo[] = [
  { unit: 'Pounds', symbol: 'lb', name: 'Pounds' },
  { unit: 'Ounces', symbol: 'oz', name: 'Ounces' },
  { unit: 'Kilograms', symbol: 'kg', name: 'Kilograms' },
  { unit: 'Grams', symbol: 'g', name: 'Grams' },
  { unit: 'NetTons', symbol: 'tn', name: 'Net Tons' },
  { unit: 'GrossTons', symbol: 'GT', name: 'Gross Tons' },
  { unit: 'MetricTons', symbol: 't', name: 'Metric Tons' },
  { unit: 'ShortTons', symbol: 'ST', name: 'Short Tons' },
  { unit: 'LongTons', symbol: 'LT', name: 'Long Tons' },
  { unit: 'Each', symbol: 'ea', name: 'Each' },
  {
    unit: 'ShortHundredweight',
    symbol: 'cwt',
    name: 'Short Hundredweight',
  },
  {
    unit: 'LongHundredweight',
    symbol: 'cwt',
    name: 'Long Hundredweight',
  },
];

export const getUnitSymbol = (unit: string | undefined): string => {
  const info = DEFAULT_WEIGHT_UNITS.find((u) => u.unit === unit);
  return info?.symbol ?? unit ?? 'lb';
};

/**
 * Convert a weight value from one unit to another.
 */
export const convertWeight = (
  value: number,
  fromUnit: Unit,
  toUnit: Unit,
): number => {
  if (fromUnit === toUnit) return value;
  const pounds = convertToPounds(value, fromUnit);
  return convertFromPounds(pounds, toUnit);
};

/**
 * Convert weight from a specific unit to pounds
 */
export const convertToPounds = (value: number, fromUnit: Unit): number => {
  const factor = CONVERSION_FACTORS[fromUnit];
  return value * factor;
};

/**
 * Convert weight from pounds to a specific unit
 */
export const convertFromPounds = (
  poundsValue: number,
  toUnit: Unit,
): number => {
  const factor = CONVERSION_FACTORS[toUnit];
  return poundsValue / factor;
};

// Using Unit type directly - fully standardized across frontend and backend

/**
 * Create a Weight object from a numeric value and unit
 */
export const createWeight = (value: number, unit: Unit): Weight => ({
  value: value,
  unit: unit,
  __typename: 'Weight',
});

/**
 * Parse a Weight object to numeric value and unit
 * Returns null if weight is null/undefined
 */
export const parseWeight = (
  weight: Weight | null | undefined,
): { value: number; unit: Unit } | null => {
  if (!weight || !weight.value) {
    return null;
  }

  const numericValue =
    typeof weight.value === 'string' ? parseFloat(weight.value) : weight.value;
  if (isNaN(numericValue)) {
    return null;
  }

  return {
    value: numericValue,
    unit: weight.unit,
  };
};

/**
 * Convert a Weight object to pounds (for backward compatibility)
 */
export const weightToPounds = (weight: Weight | null | undefined): number => {
  const parsed = parseWeight(weight);
  if (!parsed) return 0;

  return convertToPounds(parsed.value, parsed.unit);
};

/**
 * Create a Weight object from pounds and a target unit
 */
export const poundsToWeight = (
  poundsValue: number,
  targetUnit: Unit,
): Weight => {
  const convertedValue = convertFromPounds(poundsValue, targetUnit);
  return createWeight(convertedValue, targetUnit);
};
